import React, { useContext, useEffect, useState } from "react";
import Layout from "../../Layout";
import BillingHeader from "../../Billing/BillingHeader";
import BillingTabs from "../../Billing/BillingTabs";
import BillingOverview from "../../Billing/BillingOverview";
import { useStoreBilling } from "../../../stores/useStoreBIlling";
import { useSubscriptions } from "../../../apiHooks/useSubscriptions";
import Text from "../../Tags/Text";
import { Badge } from "../../ui/badge";
import Image from "../../Tags/Image";
import { Button } from "../../ui/button";
import { DeleteIcon, MoreVertical, Plus, RefreshCw, Trash, Trash2, Trash2Icon } from "lucide-react";
import BillingHistory from "./BillingHistory";
import PrintDate from "../../Tags/PrintDate";
import { Skeleton } from "../../ui/skeleton";
import { useNavigate } from "react-router-dom";
import { cancelSubscripton, managePaymentMethod, revokeCancellation } from "../../../apis/workspace/billing";
import { useMutation, useQueryClient } from "react-query";
import useRoleCheck from "../../../hooks/useRoleCheck";
import PageHeader from "../../PageHeader";
import BadgeTag from "../../Tags/BadgeTag";
import TooltipWrapper from "../../TooltipWrapper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import toast from "react-hot-toast";
import ConfirmModal from "../../Modals/ConfirmModal";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeWorkspace } = useRoleCheck();
  const { subscriptions, trials, payment_methods, one_time_purchases, summary } = useStoreBilling();
  const { isLoading, refetch } = useSubscriptions(activeWorkspace?.id);
  const captureEvent = useCaptureEvent();

  useEffect(() => {
    captureEvent("BP_PageView");
  }, [captureEvent]);

  const hasNoBilling = () => {
    if (
      subscriptions &&
      subscriptions.length === 0 &&
      trials &&
      trials.length === 0 &&
      one_time_purchases &&
      one_time_purchases.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const managePaymentMethodFn = useMutation("manage_payment_method", managePaymentMethod, {
    onSuccess(data, variables, context) {
      console.log("manage_payment_method", data);
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError(error) {
      console.log(error);
    },
  });
  const handleManagePaymentMethod = () => {
    if (managePaymentMethodFn.isLoading) {
      return;
    }
    captureEvent("BP_ManagePaymentMethod");
    managePaymentMethodFn.mutate({
      data: {
        return_url: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/billing/subscriptions`,
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [selectedSubscription, setSelectedSubscription] = useState({});

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmModalType, setOpenConfirmModalType] = useState("");
  const confirmModalState = (data) => {
    setOpenConfirmModal(data.false);
    //console.log(data);
    if (data && data.confirm && data.type && data.type === "delete") {
      handleCancelSubscripton();
    }
  };

  const cancelSubscriptonFn = useMutation("cancel_subscription", cancelSubscripton, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      setOpenConfirmModal(false);
      refetch();
      captureEvent("BP_CancelSuccess", {
        subscription_id: variables?.id,
      });
    },
    onError(error, variables) {
      toast.error(error?.message || "Failed to cancel subscription");
      captureEvent("BP_CancelFailure", {
        subscription_id: variables?.id,
        error: error?.message || "unknown_error",
      });
    },
  });
  const handleCancelSubscripton = () => {
    captureEvent("BP_CancelConfirm", {
      subscription_id: selectedSubscription?.stripe_id,
    });
    cancelSubscriptonFn.mutate({
      id: selectedSubscription?.stripe_id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  // revoke cancellation
  const revokeCancellationFn = useMutation("revoke_subscription", revokeCancellation, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      refetch();
      captureEvent("BP_RevokeSuccess", {
        subscription_id: variables?.id,
      });
    },
    onError(error, variables) {
      toast.error(error?.message || "Failed to revoke cancellation");
      captureEvent("BP_RevokeFailure", {
        subscription_id: variables?.id,
        error: error?.message || "unknown_error",
      });
    },
  });
  const handleRevokeCancellation = (id) => {
    captureEvent("BP_RevokeClick", {
      subscription_id: id,
    });
    revokeCancellationFn.mutate({
      id: id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const Loader = () => {
    return (
      <>
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-normal">
                        <span className="inline-flex items-center">Recurring subscriptions</span>
                      </th>
                      <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Quantity
                      </th>
                      <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Valid until
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    <tr>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                    </tr>
                    <tr>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                      <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                        <Skeleton className="h-8 w-full" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  return (
    <Layout>
      <PageHeader heading="Subscriptions" description="View and monitor credit usage across the workspace" />
      <BillingOverview
        clickUpgrade={(e) => {
          e.preventDefault();
          captureEvent("BP_ClickUpgrade");
          navigate(`/${activeWorkspace?.slug}/settings/billing/plans`);
        }}
      />
      <BillingTabs activeTab="Billing" />
      {!isLoading && hasNoBilling() ? (
        <>
          <div className="flex justify-center items-center flex-col mt-28">
            <Image src="/images/icons/billing/no-subscriptions.svg" alt="Img" className="no-invert" />
            <p className="text-base text-gray-900 font-semibold mt-8">You have no active subscriptions</p>
          </div>
        </>
      ) : (
        <div>
          <Text size="lg">Subscriptions</Text>
          {isLoading && <Loader />}
          {subscriptions && subscriptions.length > 0 && (
            <div className="mt-6 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden border border-border rounded-[--radius]">
                    <table className="min-w-full divide-y divide-border table-fixed">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-normal">
                            <span className="inline-flex items-center">Recurring subscriptions</span>
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Quantity
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Valid until
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border bg-background">
                        {subscriptions.map((item) => (
                          <tr>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.name}
                              {item?.is_scheduled_to_cancel && (
                                <TooltipWrapper
                                  content={<>{item?.cancel_at && <PrintDate date={item?.cancel_at} />}</>}
                                >
                                  <Badge className="rounded-md ml-2">Set to cancel</Badge>
                                </TooltipWrapper>
                              )}
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.quantity}
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              <div className="flex w-full justify-between items-center">
                                <div> {item?.valid_until ? <PrintDate date={item?.valid_until} /> : "-"}</div>
                                <div className="">
                                  {!item?.is_scheduled_to_cancel ? (
                                    <DropdownMenu
                                      open={isMenuOpen}
                                      onOpenChange={(open) => setIsMenuOpen(open)}
                                    >
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          type="button"
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#535862] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-200"
                                        >
                                          <MoreVertical className="h-5 w-5" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={() => {
                                            console.log("item", item);
                                            setSelectedSubscription(item);
                                            setOpenConfirmModal(true);
                                            setOpenConfirmModalType("delete");
                                            captureEvent("BP_CancelClick", {
                                              subscription_id: item?.stripe_id,
                                            });
                                          }}
                                          className="text-error-600 focus:text-error-600 whitespace-pre"
                                          disabled={item?.is_scheduled_to_cancel}
                                        >
                                          <Trash2Icon width={16} height={16} /> Cancel subscription
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  ) : (
                                    <Button
                                      variant="purple_outline"
                                      onClick={() => {
                                        handleRevokeCancellation(item?.stripe_id);
                                      }}
                                      disabled={revokeCancellationFn.isLoading}
                                    >
                                      <RefreshCw
                                        width={18}
                                        height={18}
                                        className={`mr-2 ${revokeCancellationFn.isLoading ? "animate-spin" : ""}`}
                                      />{" "}
                                      Revoke cancellation
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          {one_time_purchases && one_time_purchases.length > 0 && (
            <div className="mt-6 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden border border-border rounded-[--radius]">
                    <table className="min-w-full divide-y divide-border table-fixed">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-normal">
                            <span className="inline-flex items-center">One-time</span>
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Quantity
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Purchased at
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border bg-background">
                        {one_time_purchases.map((item) => (
                          <tr>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.name}
                              <BadgeTag dot text={item?.status} className="rounded-md ml-2" />
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.quantity}
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.purchased_at ? <PrintDate date={item?.purchased_at} /> : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          {trials && trials.length > 0 && (
            <div className="mt-6 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden border border-border rounded-[--radius]">
                    <table className="min-w-full divide-y divide-border table-fixed">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-normal">
                            <span className="inline-flex items-center">Trial</span>
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Quantity
                          </th>
                          <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                            Valid until
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border bg-background">
                        {trials.map((item) => (
                          <tr>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.name}
                              <BadgeTag dot text={item?.status} className="rounded-md ml-2" />
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.quantity}
                            </td>
                            <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                              {item?.expires_at ? <PrintDate date={item?.expires_at} /> : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="border-b border-border my-6"></div>
          {payment_methods && payment_methods.length && payment_methods.length > 0 ? (
            <div>
              <Text size="lg">Payment methods</Text>
              <div className="mt-0">
                {payment_methods &&
                  payment_methods.length &&
                  payment_methods.length > 0 &&
                  payment_methods.map((item) => (
                    <div className="border-border border inline-flex items-start space-x-4 justify-between bg-background p-3 rounded-xl mr-4 mt-4 relative pr-16">
                      <Image
                        src={`/images/icons/cards/${item?.card?.brand}.svg`}
                        className="inline-block no-invert"
                      />
                      <div className="pr-5">
                        <span className="text-gray-600 font-bold">
                          {item?.card?.brand} ending in {item?.card?.last4}
                        </span>
                        <Text className="mb-2 mt-1 text-gray-500">
                          Expiry {item?.card?.exp_month}/{item?.card?.exp_year}
                        </Text>
                        {!item?.is_default && (
                          <span
                            className={`font-bold mr-2  ${managePaymentMethodFn.isLoading ? "opacity-75" : "cursor-pointer"}`}
                            onClick={handleManagePaymentMethod}
                          >
                            Set as default
                          </span>
                        )}
                        <span
                          className={`font-bold text-brand-700 ${managePaymentMethodFn.isLoading ? "opacity-75" : "cursor-pointer"}`}
                          onClick={handleManagePaymentMethod}
                        >
                          Edit
                        </span>
                      </div>
                      {item?.is_default && (
                        <Badge variant="gray" className="absolute right-3 top-3">
                          Default
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>
              <div>
                <span
                  className={`inline-flex items-center font-bold text-sm mt-4 ${managePaymentMethodFn.isLoading ? "opacity-75" : "cursor-pointer"}`}
                  onClick={handleManagePaymentMethod}
                >
                  <Plus className="mr-1" width={18} height={18} /> Add new payment method
                </span>
              </div>
              <div className="border-b border-border my-6"></div>
            </div>
          ) : (
            <></>
          )}

          <BillingHistory activeWorkspace={activeWorkspace} />
        </div>
      )}
      <ConfirmModal
        openModal={openConfirmModal}
        modalType={openConfirmModalType}
        confirmModalState={confirmModalState}
      />
    </Layout>
  );
}
